import { useState } from "react";
import { Link } from "react-router-dom";
import { Loader2, CheckCircle2, Award } from "lucide-react";
import { http, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const DOMAINS = ["Formations", "Articles / conseils", "Webinaires", "Conférences", "Autre"];
const MAX_PRES = 500;

export default function ExpertsPage() {
  const [form, setForm] = useState({
    name: "", profession: "", expertise: "", location: "",
    presentation: "", experience: "", profile_link: "", email: "",
  });
  const [domains, setDomains] = useState([]);
  const [photo, setPhoto] = useState(null);
  const [cv, setCv] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const toggleDomain = (d) =>
    setDomains((cur) => (cur.includes(d) ? cur.filter((x) => x !== d) : [...cur, d]));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      fd.append("domains", domains.join(","));
      if (photo) fd.append("photo", photo);
      if (cv) fd.append("cv", cv);
      await http.post("/experts/apply", fd);
      setSent(true);
      toast.success("Profil envoyé", { description: "Merci ! Notre équipe reviendra vers vous rapidement." });
    } catch (err) {
      toast.error("Erreur", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="experts-page">
      <section className="container-x pt-16 pb-10">
        <div className="max-w-3xl">
          <div className="eyebrow mb-3">Réseau d'experts</div>
          <h1 className="font-display text-4xl sm:text-5xl font-bold text-slate-900 tracking-tight leading-[1.1]">
            Rejoindre le réseau d'experts.
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed">
            Vous êtes expert de la petite enfance, du développement de l'enfant ou d'un domaine associé ?
            Rejoignez le réseau d'experts de Beehive Academy et partagez votre expertise à travers nos formations.
          </p>
        </div>
      </section>

      <section className="container-x pb-8">
        <div className="card-soft p-8 max-w-2xl" data-testid="community-contribution-block">
          <div className="eyebrow mb-2">Communauté</div>
          <h2 className="font-display font-bold text-xl text-slate-900">Contribuer à la communauté</h2>
          <p className="text-sm text-slate-600 mt-2 leading-relaxed">
            Vous souhaitez partager une réflexion, une pratique ou un article avec notre communauté ?
            Vous pouvez nous transmettre votre contribution lors de votre candidature ou directement depuis votre
            espace expert. Les contributions sélectionnées pourront être partagées avec la communauté Beehive Academy
            afin de faire circuler les connaissances et de nourrir les échanges autour de l'enfance et de l'éducation.
          </p>
          <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-100 p-5">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2.5">Conditions de contribution</div>
            <ul className="space-y-2 text-sm text-slate-700">
              <li className="flex gap-2.5"><CheckCircle2 size={15} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} />La contribution doit être en lien avec l'enfance, le développement ou l'éducation.</li>
              <li className="flex gap-2.5"><CheckCircle2 size={15} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} />Elle doit s'appuyer sur une expertise, une expérience ou des connaissances pertinentes.</li>
              <li className="flex gap-2.5"><CheckCircle2 size={15} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} />La contribution doit être originale et respecter les droits d'auteur.</li>
              <li className="flex gap-2.5"><CheckCircle2 size={15} className="text-brand-600 shrink-0 mt-0.5" strokeWidth={1.75} />Beehive Academy se réserve la possibilité de sélectionner les contributions publiées.</li>
            </ul>
          </div>
          <Link to="/blog" data-testid="experts-contribute-link" className="btn-outline mt-5 text-sm">Proposer un article</Link>
        </div>
      </section>

      <section className="container-x pb-20">
        {sent ? (
          <div className="card-soft p-10 max-w-2xl flex items-start gap-4" data-testid="experts-success">
            <CheckCircle2 className="text-emerald-600 shrink-0 mt-1" strokeWidth={1.75} />
            <div>
              <h2 className="font-display font-bold text-xl text-slate-900">Merci pour votre proposition.</h2>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">Votre profil a bien été transmis à notre équipe. Nous reviendrons vers vous rapidement pour échanger sur les collaborations possibles.</p>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="card-soft p-8 max-w-2xl space-y-4" data-testid="experts-form">
            <div className="flex items-center gap-3 mb-2">
              <div className="grid place-items-center h-11 w-11 rounded-2xl bg-brand-500 text-white"><Award strokeWidth={1.75} size={20} /></div>
              <h2 className="font-display font-bold text-xl text-slate-900">Déposer mon profil</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input data-testid="expert-app-name" required placeholder="Nom et prénom" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
              <Input data-testid="expert-app-profession" required placeholder="Profession / fonction" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input data-testid="expert-app-expertise" required placeholder="Domaine d'expertise" value={form.expertise} onChange={(e) => setForm({ ...form, expertise: e.target.value })} />
              <Input data-testid="expert-app-location" placeholder="Pays / ville" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} />
            </div>
            <div>
              <Textarea data-testid="expert-app-presentation" required rows={4} maxLength={MAX_PRES}
                placeholder="Présentation courte (500 caractères maximum)"
                value={form.presentation}
                onChange={(e) => setForm({ ...form, presentation: e.target.value.slice(0, MAX_PRES) })} />
              <div className="mt-1 text-right text-xs text-slate-500">{form.presentation.length} / {MAX_PRES} caractères</div>
            </div>
            <Textarea data-testid="expert-app-experience" rows={3} placeholder="Parcours et expériences clés" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} />
            <div>
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Domaines dans lesquels vous souhaitez intervenir</div>
              <div className="flex flex-wrap gap-2">
                {DOMAINS.map((d) => (
                  <button type="button" key={d} onClick={() => toggleDomain(d)} data-testid={`expert-app-domain-${d.split(" ")[0].toLowerCase()}`}
                    className={`rounded-full px-4 py-2 text-xs font-semibold transition-colors ${domains.includes(d) ? "bg-brand-500 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>
                    {d}
                  </button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <Input data-testid="expert-app-link" type="url" placeholder="Lien professionnel (LinkedIn, site, publication…)" value={form.profile_link} onChange={(e) => setForm({ ...form, profile_link: e.target.value })} />
              <Input data-testid="expert-app-email" type="email" required placeholder="Adresse e-mail professionnelle" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">Photo professionnelle</label>
                <input data-testid="expert-app-photo" type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                  className="mt-1.5 block w-full text-xs text-slate-600 file:mr-3 file:rounded-full file:border-0 file:bg-brand-50 file:px-4 file:py-2 file:text-xs file:font-semibold file:text-brand-700 hover:file:bg-brand-100" />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-widest text-slate-500">CV ou présentation (facultatif)</label>
                <input data-testid="expert-app-cv" type="file" accept=".pdf,.doc,.docx" onChange={(e) => setCv(e.target.files?.[0] || null)}
                  className="mt-1.5 block w-full text-xs text-slate-600 file:mr-3 file:rounded-full file:border-0 file:bg-brand-50 file:px-4 file:py-2 file:text-xs file:font-semibold file:text-brand-700 hover:file:bg-brand-100" />
              </div>
            </div>
            <button data-testid="expert-app-submit" disabled={loading} className="btn-primary w-full disabled:opacity-70">
              {loading ? <Loader2 size={16} className="animate-spin" /> : "→ Déposer mon profil"}
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
